import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageSquare, CheckCircle2, Save, Unlink2, Send, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";

export default function ClickSendSettings() {
  const [status, setStatus] = useState(null);
  const [username, setUsername] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [defaultFrom, setDefaultFrom] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/sms/settings");
      setStatus(r.data);
      if (r.data?.configured) {
        setUsername(r.data.username || "");
        setDefaultFrom(r.data.default_from || "");
      }
    } catch (e) {
      toast.error("Could not load ClickSend status", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function save() {
    if (!username.trim()) {
      toast.error("Enter your ClickSend username");
      return;
    }
    if (!apiKey.trim() && !status?.configured) {
      toast.error("Paste your ClickSend API key");
      return;
    }
    setSaving(true);
    try {
      const body = { username: username.trim(), default_from: defaultFrom.trim() || null };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      await api.post("/sms/settings", body);
      toast.success("ClickSend settings saved");
      setApiKey("");
      await refresh();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    setTesting(true);
    try {
      const body = {};
      if (username.trim()) body.username = username.trim();
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const r = await api.post("/sms/settings/test", body);
      if (r.data?.ok) {
        const bal = r.data.account?.balance;
        toast.success("ClickSend connected", {
          description: bal != null ? `Balance: $${Number(bal).toFixed(2)} ${r.data.account?.username ? "· " + r.data.account.username : ""}` : "Credentials are valid",
        });
      } else {
        toast.error("ClickSend test failed", { description: r.data?.message || "Unknown error" });
      }
    } catch (e) {
      toast.error("Test failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  }

  async function disconnect() {
    if (!confirm("Disconnect ClickSend? Your credentials will be removed from this server.")) return;
    setDisconnecting(true);
    try {
      await api.post("/sms/settings/disconnect");
      toast.success("ClickSend disconnected");
      setUsername("");
      setApiKey("");
      setDefaultFrom("");
      await refresh();
    } catch (e) {
      toast.error("Disconnect failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setDisconnecting(false);
    }
  }

  const configured = !!status?.configured;

  return (
    <Card className="p-6 rounded-lg border border-zinc-200 shadow-none" data-testid="clicksend-settings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          <MessageSquare className="h-3.5 w-3.5" /> ClickSend SMS
        </div>
        {configured && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Configured
          </span>
        )}
      </div>

      <p className="mt-2 text-sm text-zinc-600">
        Send single or batch SMS via ClickSend. Sign in at clicksend.com → Account → API Credentials to grab
        your username and API key.
      </p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">ClickSend Username</Label>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="border-zinc-300 mt-1.5 font-mono-num"
            placeholder="your-clicksend-username"
            data-testid="clicksend-username"
          />
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">API Key</Label>
          <div className="relative mt-1.5">
            <Input
              type={showKey ? "text" : "password"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="border-zinc-300 font-mono-num pr-9"
              placeholder={configured ? "(stored — paste a new key to replace)" : "ClickSend API key"}
              data-testid="clicksend-api-key"
            />
            <button
              type="button"
              onClick={() => setShowKey((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900"
              tabIndex={-1}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label className="text-2xs uppercase tracking-wider text-zinc-500">Sender ID (optional)</Label>
          <Input
            value={defaultFrom}
            onChange={(e) => setDefaultFrom(e.target.value)}
            className="border-zinc-300 mt-1.5 font-mono-num"
            placeholder="Lebbrehab or +614..."
            data-testid="clicksend-sender-id"
          />
          <p className="mt-1 text-2xs text-zinc-500">Up to 11 chars (alpha) or an E.164 number you've registered.</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={save} disabled={saving} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="clicksend-save-btn">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
        <Button onClick={test} disabled={testing} variant="outline" className="border-zinc-300" data-testid="clicksend-test-btn">
          <Send className="h-4 w-4 mr-1.5" /> {testing ? "Testing…" : "Test connection"}
        </Button>
        {configured && (
          <Button onClick={disconnect} disabled={disconnecting} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="clicksend-disconnect-btn">
            <Unlink2 className="h-4 w-4 mr-1.5" /> {disconnecting ? "Disconnecting…" : "Disconnect"}
          </Button>
        )}
        {status?.updated_at && (
          <span className="ml-auto self-center text-2xs text-zinc-500 font-mono-num">
            Updated {fmtDateTime(status.updated_at)}
          </span>
        )}
      </div>
    </Card>
  );
}
